import type { PlatformTypeName } from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";
import { ChannelIcon } from "./ChannelIcon.js";
import { channelBadgeClassName } from "./channel-presentation.js";

/**
 * Inline channel identity used by Contacts table badges and Inbox filter chips.
 * Colored circular fill + white glyph. FACEBOOK renders as Messenger (ChannelIcon).
 */
const IDENTITY_SIZES = {
  /** Contacts `LinkedPlatformBadges` table cells. */
  table: {
    box: "inline-grid size-[1.125rem] place-items-center rounded-full",
    glyphPx: 9,
  },
  /** Inbox conversation filter select / radio chips. */
  filter: {
    box: "inline-flex size-5 items-center justify-center rounded-full ring-1 ring-white/80",
    glyphPx: 11,
  },
  /** Inline marks inside text lines (thread header identity, active filter chips). */
  inline: {
    box: "inline-grid size-3.5 shrink-0 place-items-center rounded-full",
    glyphPx: 8,
  },
  /** Linked channel identity rows in Inbox Details. */
  row: {
    box: "inline-grid size-[1.375rem] shrink-0 place-items-center rounded-full",
    glyphPx: 12,
  },
  /** Channel profile rows in the contact lookup dialog. */
  profile: {
    box: "inline-grid size-7 shrink-0 place-items-center rounded-full",
    glyphPx: 15,
  },
} as const;

export type ChannelIdentityBadgeSize = keyof typeof IDENTITY_SIZES;

export function ChannelIdentityBadge({
  platformType,
  size = "table",
  label,
  muted = false,
  className,
}: {
  platformType: PlatformTypeName;
  size?: ChannelIdentityBadgeSize;
  label?: string;
  /** Same mark, without the brand fill. Entitlement off is not connection health. */
  muted?: boolean;
  className?: string;
}): React.JSX.Element {
  const mark = IDENTITY_SIZES[size];

  return (
    <span
      className={cn(
        mark.box,
        muted ? "bg-surface-muted text-subtle" : channelBadgeClassName(platformType),
        className,
      )}
      title={label}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-presentation={muted ? "muted" : "brand"}
    >
      <ChannelIcon
        platformType={platformType}
        size={mark.glyphPx}
        className={muted ? "text-subtle" : "text-white"}
      />
    </span>
  );
}
